import { FC, memo } from 'react'
import { styled, Style } from 'inlines'
import DOMPurify from 'dompurify'
import { TEXT_VARIANT_STYLES, Typography } from '../utils/typography'
import { ThemeColors, useTheme } from '../providers/useTheme'

export type HtmlProps = {
  children: string
  variant?: Typography
  color?: string | 'auto' | ThemeColors
  align?: Style['textAlign']
  isNotSelectable?: boolean
  isFocusable?: boolean
  style?: Style
  role?: React.AriaRole
  'aria-label'?: string
  'aria-labelledby'?: string
}

export const Html: FC<HtmlProps> = memo(
  ({
    children,
    variant = 'display-regular',
    color = 'auto',
    align = 'left',
    isNotSelectable = false,
    isFocusable = false,
    style,
    role,
    'aria-label': ariaLabelProp,
    'aria-labelledby': ariaLabelledByProp,
  }) => {
    const { colors } = useTheme()

    const sanitizedHtml = (() => {
      DOMPurify.addHook('afterSanitizeAttributes', (node) => {
        if (node.tagName === 'A') {
          node.setAttribute('target', '_blank')
          node.setAttribute('rel', 'noopener noreferrer')
        }
      })
      const result = DOMPurify.sanitize(children)
      DOMPurify.removeHook('afterSanitizeAttributes')
      return result
    })()

    const textColor: string =
      color === 'auto' ? colors.textSecondary : (color as string)

    const baseStyle: Style = {
      position: 'relative',
      textAlign: align,
      fontVariantNumeric: 'tabular-nums',
      lineHeight: 'normal',
      overflowWrap: 'break-word',
      textRendering: 'optimizeLegibility',
      WebkitFontSmoothing: 'antialiased',
      MozOsxFontSmoothing: 'grayscale',
      WebkitTextSizeAdjust: '100%',
      MozTextSizeAdjust: '100%',
      textSizeAdjust: '100%',
      textDecoration: 'none',
      userSelect: isNotSelectable ? 'none' : 'text',
      whiteSpace: 'normal',
      ...TEXT_VARIANT_STYLES[variant],
      color: textColor,
      fontSize: 16,

      fontFamily:
        'Inter, Geist, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
      fontWeight: 400,
      fontStyle: 'normal',
      '& p': {
        fontSize: 16,
        color: colors.textSecondary,
        marginBottom: '16px',
        lineHeight: '24px',
      },
      '& h1': {
        marginBottom: '16px',
        color: colors.textSecondary,
        fontSize: 22,
      },
      '& h2': {
        marginBottom: '16px',
        color: colors.textSecondary,
        fontSize: 20,
      },
      '& h3': {
        marginBottom: '16px',
        color: colors.textSecondary,
        fontSize: 18,
      },
      '& a': {
        textDecoration: 'none',
        color: colors.primaryAccent,
      },
      ...style,
    }

    return (
      <styled.div
        role={role}
        aria-label={ariaLabelProp}
        aria-labelledby={ariaLabelledByProp}
        tabIndex={isFocusable ? 0 : undefined}
        style={baseStyle}
        dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
      />
    )
  },
)

Html.displayName = 'Html'
